/**
 * PATH: apps/quote/src/components/builder/panels/inspector/AIRecommendationsTab.tsx
 * PURPOSE: AI recommendations tab for the row inspector
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sparkles, Loader2, CheckCircle2 } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteLineItem, type AISuggestion } from '@/lib/quoteApi'

interface AIRecommendationsTabProps {
  lineItem: QuoteLineItem
  onApply?: (suggestion: AISuggestion) => void
}

export default function AIRecommendationsTab({
  lineItem,
  onApply,
}: AIRecommendationsTabProps) {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['aiSuggestions', lineItem.description],
    queryFn: () => quoteApi.getAISuggestions(lineItem.description || ''),
    enabled: !!lineItem.description && lineItem.description.length >= 3,
    staleTime: 60 * 1000,
  })

  useEffect(() => {
    if (data) setSuggestions(data)
  }, [data])

  const handleApply = (suggestion: AISuggestion) => {
    quoteApi.recordAIFeedback(suggestion.id, 'accepted')
    onApply?.(suggestion)
  }

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-amber-500" />
            AI Recommendations
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pricing and material suggestions for this item
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md"
        >
          <Loader2 className={cn('w-4 h-4 text-slate-400', isLoading && 'animate-spin')} />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <Sparkles className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No suggestions available</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            Try a more specific description
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {suggestions.slice(0, 5).map((suggestion) => (
            <div
              key={suggestion.id}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                    {suggestion.item_name}
                  </p>
                  <p className="text-xs text-slate-500">{suggestion.source}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-blue-600">
                    {formatCurrency(suggestion.unit_price || 0)}
                  </p>
                  <p className="text-xs text-slate-400">
                    {suggestion.unit} • {Math.round(suggestion.confidence)}%
                  </p>
                </div>
              </div>
              {suggestion.explanation && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 italic">
                  {suggestion.explanation}
                </p>
              )}
              <button
                onClick={() => handleApply(suggestion)}
                className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <CheckCircle2 className="w-3 h-3" />
                Apply Suggestion
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pricing Context */}
      <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
        <h4 className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
          Current Line Item Pricing
        </h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
            <p className="text-slate-500">Rate/unit</p>
            <p className="font-medium text-slate-900 dark:text-white">
              {formatCurrency(Number(lineItem.rate_per_unit) || 0)}
            </p>
          </div>
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
            <p className="text-slate-500">Margin</p>
            <p className="font-medium text-slate-900 dark:text-white">
              {Number(lineItem.margin_percent || 0).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
